import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  MapPin,
  MessageCircle,
  Phone,
  Shield,
  Sparkles,
  Star,
  Users,
  Zap,
} from 'lucide-react';
import { CLINIC_INFO, FIRST_VISIT_STEPS, IMAGES, SERVICES, TESTIMONIALS } from '../data/mock';
import AppointmentForm from '../components/shared/AppointmentForm';

const HomePage = () => {
  const navigate = useNavigate();
  const featuredServices = SERVICES.slice(0, 4);
  const featuredTestimonials = TESTIMONIALS.slice(0, 3);

  const whatsappMessage = encodeURIComponent(
    `Hola, quiero agendar una cita en Áurea Dental. Mi nombre es ___, busco ___, mi disponibilidad es ___.`
  );

  const quickCards = [
    {
      icon: AlertCircle,
      title: 'Urgencias hoy',
      description: 'Atención el mismo día (sujeto a disponibilidad).',
      link: '/servicios#urgencias',
      highlight: true,
    },
    {
      icon: Sparkles,
      title: 'Estética dental',
      description: 'Diseño de sonrisa, carillas y blanqueamiento.',
      link: '/servicios#estetica',
    },
    {
      icon: Shield,
      title: 'Planes de pago',
      description: 'MSI y opciones flexibles.',
      link: '/servicios#planes',
    },
    {
      icon: MapPin,
      title: `${CLINIC_INFO.city}, ${CLINIC_INFO.state}`,
      description: 'Acceso fácil y estacionamiento.',
      link: '/#ubicacion',
    },
  ];

  return (
    <div>
      <section className="bg-white py-14 lg:py-20 relative overflow-hidden">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-14 lg:gap-16 items-center">
            <div className="md:col-span-5 animate-fade-in-up">
              <h1 className="text-display mb-5 lg:[font-size:44px]">
                Tu sonrisa merece
                <span className="block text-[var(--color-accent)] lg:whitespace-nowrap">atención de excelencia</span>
              </h1>

              <p className="mt-5 text-lg md:text-xl text-[var(--text-secondary)] max-w-[42ch]">
                Tecnología digital, especialistas certificados y plan por escrito.
              </p>

              <div className="mt-5 text-sm text-[var(--text-muted)] flex flex-wrap items-center gap-3">
                <a
                  href={CLINIC_INFO.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-[var(--text-primary)] transition-colors"
                  aria-label="Ver reseñas en Google"
                >
                  <Star size={16} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
                  <span>
                    {CLINIC_INFO.googleRating}/5 ({CLINIC_INFO.totalReviews} reseñas)
                  </span>
                </a>
                <span aria-hidden="true">•</span>
                <span>{CLINIC_INFO.yearsExperience}+ años</span>
                <span aria-hidden="true">•</span>
                <span>
                  {CLINIC_INFO.patientsServed} pacientes
                  {/* TODO: Confirmar cifra real de pacientes atendidos antes de usarla en campañas. */}
                </span>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href={CLINIC_INFO.calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Agendar cita en línea"
                  className="btn btn-primary btn-lg justify-center w-full sm:w-auto"
                >
                  <Calendar size={20} />
                  Agendar cita
                </a>
                <a
                  href={`https://wa.me/${CLINIC_INFO.whatsapp}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Agendar por WhatsApp"
                  className="btn btn-whatsapp btn-lg justify-center w-full sm:w-auto"
                >
                  <MessageCircle size={20} />
                  WhatsApp
                </a>
              </div>

              <a
                href="#agendar"
                className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                Ver disponibilidad
                <ArrowRight size={14} />
              </a>
            </div>

            <div className="md:col-span-7 relative">
              <div className="aspect-[16/11] rounded-3xl overflow-hidden shadow-lg">
                <img
                  src={IMAGES.hero}
                  alt="Clínica dental moderna de Áurea Dental"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="absolute -bottom-6 right-6 bg-white p-5 rounded-xl shadow-lg max-w-[240px] hidden lg:block border border-[var(--border-light)]">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
                  ))}
                </div>
                <p className="text-sm text-[var(--text-secondary)] italic mb-2">“Excelente atención y resultados”</p>
                <p className="text-xs text-[var(--text-muted)]">{CLINIC_INFO.patientsServed} pacientes atendidos</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-secondary)] py-6 mt-10 lg:mt-14">
        <div className="container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
            {quickCards.map((card) => {
              if (card.highlight) {
                return (
                  <div
                    key={card.title}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(card.link)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(card.link);
                      }
                    }}
                    className="card flex items-start gap-4 p-5 group h-full border-[var(--color-accent)] bg-[var(--color-accent-bg)] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[var(--color-accent)] text-white">
                      <card.icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1 group-hover:text-[var(--color-accent)] transition-colors">
                        {card.title}
                      </h3>
                      <p className="text-xs text-[var(--text-muted)] leading-relaxed">{card.description}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <a
                          href={`tel:${CLINIC_INFO.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs px-3 py-1.5 rounded-full bg-white border border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
                          aria-label="Llamar por urgencia"
                        >
                          Llamar
                        </a>
                        <a
                          href={`https://wa.me/${CLINIC_INFO.whatsapp}?text=${encodeURIComponent(
                            'Hola, tengo una urgencia dental. ¿Tienen disponibilidad hoy?'
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs px-3 py-1.5 rounded-full bg-white border border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
                          aria-label="Enviar WhatsApp por urgencia"
                        >
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link key={card.title} to={card.link} className="card flex items-start gap-4 p-5 group h-full">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[var(--bg-tertiary)] text-[var(--text-primary)]">
                    <card.icon size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1 group-hover:text-[var(--color-accent)] transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-xs text-[var(--text-muted)] leading-relaxed">{card.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-h2 mb-4">¿Por qué elegirnos?</h2>
            <p className="text-body-lg">Un estándar alto en diagnóstico, planificación y ejecución — con atención clara y humana.</p>
            <div className="mt-4">
              <a
                href={CLINIC_INFO.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[var(--color-accent)] hover:underline"
              >
                <Star size={16} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
                Ver reseñas en Google
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: 'Especialistas certificados', description: 'Cédulas verificables y formación continua.' },
              { icon: Zap, title: 'Tecnología digital', description: 'Radiografías digitales, escáner 3D y planificación.' },
              { icon: Shield, title: 'Seguridad clínica', description: 'Protocolos y esterilización estricta.' },
              { icon: Clock, title: 'Agenda puntual', description: 'Confirmación por WhatsApp y recordatorios.' },
            ].map((item) => (
              <div key={item.title} className="card p-6 text-center h-full">
                <div className="w-14 h-14 mx-auto mb-4 bg-[var(--color-accent-bg)] rounded-xl flex items-center justify-center">
                  <item.icon size={24} className="text-[var(--color-accent)]" />
                </div>
                <h3 className="text-h4 mb-2">{item.title}</h3>
                <p className="text-body-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-secondary)] py-12">
        <div className="container">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <h2 className="text-h2 mb-3">Tratamientos</h2>
              <p className="text-body-lg max-w-xl">Soluciones integrales para tu salud bucal, desde prevención hasta estética avanzada.</p>
            </div>
            <Link to="/servicios" className="btn btn-secondary">
              Ver todos los servicios <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredServices.map((service) => (
              <Link key={service.id} to={`/servicios#${service.id}`} className="card card-elevated p-0 overflow-hidden group">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="text-h4 mb-2 group-hover:text-[var(--color-accent)] transition-colors">{service.title}</h3>
                  <p className="text-body-sm mb-4">{service.shortDescription}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[var(--text-muted)]">
                      {service.duration} • {service.sessions}
                    </span>
                    <span className="text-sm font-medium text-[var(--text-primary)] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[var(--border-light)] group-hover:border-[var(--color-accent)] group-hover:text-[var(--color-accent)] transition-colors">
                      Ver más <ChevronRight size={16} />
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-3 truncate">Ideal para: {service.recommendedFor}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-h2 mb-4">Cómo funciona tu primera visita</h2>
            <p className="text-body-lg">Un proceso simple y sin presión. Tú decides cuándo avanzar.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {FIRST_VISIT_STEPS.map((step, index) => (
              <div key={step.step} className="relative">
                {index < FIRST_VISIT_STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+32px)] w-[calc(100%-64px)] h-0.5 bg-[var(--border-light)]" />
                )}

                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[var(--color-primary)] text-white rounded-full flex items-center justify-center text-2xl font-bold relative z-10">
                    {step.step}
                  </div>
                  <h3 className="text-h4 mb-2">{step.title}</h3>
                  <p className="text-body-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <a href={CLINIC_INFO.calendlyUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              <Calendar size={18} />
              Agendar cita
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-secondary)] py-12">
        <div className="container">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
                  ))}
                </div>
                <span className="text-sm font-semibold">{CLINIC_INFO.googleRating}/5 en Google</span>
                <span className="text-sm text-[var(--text-muted)]">({CLINIC_INFO.totalReviews} reseñas)</span>
              </div>
              <h2 className="text-h2 mb-3">Lo que dicen nuestros pacientes</h2>
            </div>
            <Link to="/testimonios" className="btn btn-secondary">
              Ver todos <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="card p-6 h-full flex flex-col">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={14} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
                    ))}
                  </div>
                  {testimonial.verified && (
                    <span className="text-xs px-2 py-1 rounded-full bg-white border border-[var(--border-light)] text-[var(--text-muted)]">
                      Google
                    </span>
                  )}
                </div>
                <p className="text-body mb-4 italic flex-1 line-clamp-3">“{testimonial.quote}”</p>
                <Link to="/testimonios" className="text-sm text-[var(--color-accent)] hover:underline self-start mb-4">
                  Leer más
                </Link>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[var(--text-primary)]">{testimonial.name}</p>
                    <p className="text-xs text-[var(--text-muted)]">{testimonial.treatment}</p>
                  </div>
                  <span className="text-xs text-[var(--text-muted)]">{testimonial.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="ubicacion" className="bg-white py-12">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-h2 mb-6">Ubicación</h2>
              <p className="text-body-lg mb-8">
                Estamos en una zona de fácil acceso en {CLINIC_INFO.city}, con estacionamiento disponible.
              </p>

              <div className="space-y-6 mb-8">
                <div className="bg-[var(--bg-secondary)] border border-[var(--border-light)] rounded-xl p-4">
                  <p className="text-sm font-medium text-[var(--text-primary)] mb-2">Antes de venir</p>
                  <ul className="text-sm text-[var(--text-muted)] space-y-1">
                    <li>• Estacionamiento disponible</li>
                    <li>• Fácil acceso en {CLINIC_INFO.city}</li>
                    <li>• Abre tu ruta desde Google Maps</li>
                  </ul>
                </div>

                <a href={CLINIC_INFO.mapsLink} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[var(--color-accent-bg)] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={22} className="text-[var(--color-accent)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[var(--text-primary)] group-hover:text-[var(--color-accent)] transition-colors">
                      {CLINIC_INFO.address}
                    </p>
                    <p className="text-sm text-[var(--color-accent)]">Ver en Google Maps</p>
                  </div>
                </a>

                <a href={CLINIC_INFO.mapsLink} target="_blank" rel="noopener noreferrer" className="btn btn-secondary justify-center w-full sm:w-auto">
                  <MapPin size={18} />
                  Abrir en Maps
                </a>

                <a href={`tel:${CLINIC_INFO.phone}`} className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[var(--color-accent-bg)] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone size={22} className="text-[var(--color-accent)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[var(--text-primary)] group-hover:text-[var(--color-accent)] transition-colors">
                      {CLINIC_INFO.phone}
                    </p>
                    <p className="text-sm text-[var(--text-muted)]">Llamar</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[var(--color-accent-bg)] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={22} className="text-[var(--color-accent)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[var(--text-primary)]">Horarios</p>
                    <p className="text-sm text-[var(--text-muted)]">{CLINIC_INFO.hours.weekdays}</p>
                    <p className="text-sm text-[var(--text-muted)]">{CLINIC_INFO.hours.saturday}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg">
              <iframe
                src={CLINIC_INFO.mapUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación de Áurea Dental"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="agendar" className="bg-[var(--bg-dark)] text-white py-12">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <h2 className="text-h2 text-white mb-4">Agenda tu cita</h2>
              <p className="text-gray-300 text-lg mb-7">
                Completa el formulario y te contactaremos para confirmar. Si prefieres, agenda directo en línea o por WhatsApp.
              </p>

              <div className="space-y-3 mb-8">
                {['Evaluación + diagnóstico + plan por escrito', 'Costos claros antes de iniciar', 'Sin compromiso ni presión'].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 size={20} className="text-[var(--color-accent)]" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>

              <a
                href={`https://wa.me/${CLINIC_INFO.whatsapp}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp btn-lg w-full sm:w-auto justify-center"
              >
                <MessageCircle size={20} />
                WhatsApp
              </a>
            </div>

            <div className="bg-white text-[var(--text-primary)] rounded-2xl p-8 shadow-xl">
              <h3 className="text-h4 mb-6">Solicita tu cita</h3>
              <AppointmentForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
